class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
    this.setHeaderCartIconAccessibility();

    // Delegated from <cart-drawer>, not bound to the button: the button
    // lives inside #CartDrawer, which renderContents() replaces wholesale
    // on every cart mutation, so a listener bound directly to it would be
    // lost on the first quantity change. The expand/collapse state itself
    // lives in the data-gift-cue attribute on this element (above the
    // replaced subtree), so it needs no save/restore here.
    this.addEventListener('click', (event) => {
      const toggle = event.target.closest('[data-gift-cue-toggle]');
      if (!toggle) return;
      const expanded = this.getAttribute('data-gift-cue') === 'expanded';
      this.setAttribute('data-gift-cue', expanded ? 'collapsed' : 'expanded');
      this.syncGiftCueToggle(toggle);
    });

    // The toggle button is re-rendered from Liquid on every Section
    // Rendering refresh (it lives inside #CartDrawer), so its
    // aria-expanded/aria-label always come back reset to the collapsed
    // default — Liquid has no way to know the client-side data-gift-cue
    // state on <cart-drawer>, since that state never reaches the
    // server-rendered section markup. There are two separate refresh code
    // paths that replace markup inside #CartDrawer (renderContents() below,
    // used after add-to-cart, and CartItems.updateQuantity() in cart.js,
    // used by the quantity stepper, which replaces .drawer__inner directly
    // and never calls renderContents()), so rather than hook both, a
    // childList/subtree observer catches the toggle being replaced by
    // either one. `attributes: true` is deliberately omitted so this
    // observer's own writes to aria-expanded/aria-label don't retrigger it.

    // TECHNICAL DEBT — paired with the renderContents() stopgap below.
    // CartItems.updateQuantity() in cart.js (the quantity stepper's path)
    // sets .innerHTML on .drawer__inner directly and never calls
    // renderContents(), so that path's scroll/focus loss can't be caught
    // at the call site without editing cart.js. A MutationObserver only
    // fires after the replace, by which point the old scroll position and
    // whatever had focus are already gone — so scroll position and the
    // focused element/caret are tracked continuously here instead, and
    // replayed by restoreScrollAndFocus() whenever the giftCueObserver
    // below reports the drawer's markup changed. Both this and the
    // renderContents() stopgap go away when morph.js lands — see
    // docs/superpowers/specs/2026-08-19-cart-drawer-design.md.
    this.lastScrollTop = 0;
    this.lastFocusedId = null;
    this.lastSelectionStart = null;
    // 'scroll' does not bubble; capture:true still lets an ancestor
    // listener see it dispatched at a scrollable descendant.
    this.addEventListener(
      'scroll',
      (event) => {
        if (event.target?.matches?.('cart-drawer-items')) {
          this.lastScrollTop = event.target.scrollTop;
        }
      },
      true,
    );
    const trackFocus = () => {
      const active = document.activeElement;
      if (!active || !this.contains(active)) return;
      // A stepper button (or any focusable element without an id) is still
      // "inside the drawer" — e.g. clicking +/- moves focus off the
      // quantity input and onto a button that has a `name` but no `id`.
      // Preserve the last known id rather than clearing it, so id-based
      // restore in restoreScrollAndFocus() survives that click. Genuinely
      // leaving the drawer is handled by the focusout listener below.
      if (active.id) this.lastFocusedId = active.id;
      this.lastSelectionStart = typeof active.selectionStart === 'number' ? active.selectionStart : null;
    };
    this.addEventListener('focusin', trackFocus);
    this.addEventListener('input', trackFocus);
    this.addEventListener('focusout', (event) => {
      // Focus left the drawer entirely (relatedTarget is the element about
      // to receive it) — stop tracking so a later refresh doesn't steal
      // focus back to a spot the shopper already moved on from.
      if (!event.relatedTarget || !this.contains(event.relatedTarget)) {
        this.lastFocusedId = null;
        this.lastSelectionStart = null;
      }
    });

    this.giftCueObserver = new MutationObserver(() => {
      this.syncGiftCueToggle();
      this.restoreScrollAndFocus();
    });
    this.giftCueObserver.observe(this, { childList: true, subtree: true });
    this.syncGiftCueToggle();
  }

  // NOTE: assets/cart.js:247-256 (CartItems.updateQuantity()'s own by-`name`
  // focus restore) already runs synchronously, right after its .innerHTML
  // replace and before this method's caller (the giftCueObserver's
  // MutationObserver microtask) ever fires. That means for the quantity-
  // stepper path, cart.js typically restores focus first and this method
  // just redoes it. That is intentional and must stay idempotent with
  // cart.js's restore — do not remove this method on the theory that
  // cart.js already "handles" focus: cart.js's by-`name` match only covers
  // paths that go through CartItems.updateQuantity(); this method is what
  // covers scroll (which cart.js never restores) and what keeps focus
  // restore working if cart.js's name-matching ever stops resolving.
  restoreScrollAndFocus() {
    const scroller = this.querySelector('cart-drawer-items');
    if (scroller) scroller.scrollTop = this.lastScrollTop;

    // Never steal focus into a closed drawer, and only restore when focus
    // was actually tracked as having been inside the drawer to begin with.
    if (!this.classList.contains('active') || !this.lastFocusedId) return;
    const restored = document.getElementById(this.lastFocusedId);
    if (!restored) return;
    restored.focus();
    // Dead in practice today: the drawer's quantity input is type="number"
    // (snippets/cart-drawer.liquid), and selectionStart reads as null on
    // that type rather than throwing, so this branch never runs. Guarded
    // correctly and left in case the input type ever changes to text/tel.
    if (this.lastSelectionStart !== null && restored.setSelectionRange) {
      restored.setSelectionRange(this.lastSelectionStart, this.lastSelectionStart);
    }
  }

  syncGiftCueToggle(toggle = this.querySelector('[data-gift-cue-toggle]')) {
    // .gift-picker is the inner element gift-campaigns-resolve emits only
    // while gifts remain to be chosen (snippets/gift-campaigns-resolve.liquid,
    // gated on `remaining > 0`) — not .gift-picker-section, the host that
    // cart-gift-zone.liquid always renders when the zone does. When it's
    // absent, any expanded/collapsed state left over from a previous picker
    // is stale: clear it so a picker that reappears later (e.g. a new gift
    // tier unlocked) starts collapsed, same as a first render. Removing the
    // attribute — rather than setting 'collapsed' — matches the CSS's
    // first-render default: cart-drawer:not([data-gift-cue]) .gift-picker-section.
    // This runs inside the childList/subtree MutationObserver's callback, but
    // removeAttribute() here doesn't retrigger it: that observer isn't
    // watching attributes (attributes: true is deliberately omitted, see the
    // constructor), and attribute changes never produce childList records
    // regardless.
    if (!this.querySelector('.gift-picker')) this.removeAttribute('data-gift-cue');

    if (!toggle) return;
    const expanded = this.getAttribute('data-gift-cue') === 'expanded';
    toggle.setAttribute('aria-expanded', String(expanded));
    if (expanded) {
      toggle.setAttribute('aria-label', toggle.dataset.giftCueCollapseLabel);
    } else {
      toggle.removeAttribute('aria-label');
    }
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    if (!cartLink) return;

    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink);
    });
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  async open(triggeredBy) {
    // `active` is NOT a synchronous guard: it is added in a setTimeout below,
    // so on its own it cannot stop a second open() that arrives before that
    // timeout fires. That mattered little while open() was synchronous, but
    // the stale refresh below is awaited, widening the window from one task
    // tick to a full network round trip. Without `opening`, a second open()
    // during an in-flight refresh sailed past this check, skipped the refresh
    // (guarded by `refreshing`, not by the method body) and showed the drawer
    // with stale content — and then the first call's `target.innerHTML =`
    // landed inside the now-visible drawer, while the second call's
    // transitionend trapped focus on nodes the first call had already
    // destroyed. `opening` is set synchronously, before any await, so a
    // second call returns immediately.
    if (this.classList.contains('active') || this.opening) return;
    this.opening = true;
    try {
      // Marked stale by page-originated cart/gift mutations (cart.js,
      // gift-manager.js) since the drawer renders — hidden — on every page,
      // including /cart. Refresh it here, before it becomes visible, rather
      // than paying for a drawer-section fetch on every cart-page mutation.
      if (this.hasAttribute('data-stale') && !this.refreshing) {
        this.refreshing = true;
        try {
          const response = await fetch(`${routes.cart_url}?section_id=cart-drawer`);
          const html = new DOMParser().parseFromString(await response.text(), 'text/html');
          const fresh = html.querySelector('.drawer__inner');
          const target = this.querySelector('.drawer__inner');
          if (fresh && target) target.innerHTML = fresh.innerHTML;
          this.removeAttribute('data-stale');
        } catch (e) {
          console.error(e);
          // A failed refresh should show a possibly-stale drawer, not
          // re-fetch on every subsequent open forever.
          this.removeAttribute('data-stale');
        } finally {
          this.refreshing = false;
        }
      }
      if (triggeredBy) this.setActiveElement(triggeredBy);
      const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
      if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);
      // here the animation doesn't seem to always get triggered. A timeout seem to help
      setTimeout(() => {
        this.classList.add('animate', 'active');
        // Released here rather than in a finally: `active` is what the guard
        // above tests, so clearing `opening` before it exists would just
        // reopen the same race one tick narrower — which is the pre-existing
        // same-tick double-open this also closes.
        this.opening = false;
      });

      this.addEventListener(
        'transitionend',
        () => {
          const containerToTrapFocusOn = this.classList.contains('is-empty')
            ? this.querySelector('.drawer__inner-empty')
            : document.getElementById('CartDrawer');
          const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
          trapFocus(containerToTrapFocusOn, focusElement);
        },
        { once: true },
      );

      document.body.classList.add('overflow-hidden');

      // cart-drawer-items is a CartItems subclass that extends createViewEventElement.
      // Its `view-event-trigger="manual"` skips auto-dispatch on connect; we fire
      // it here when the drawer opens, with `context: 'dialog'` from the payload attribute.
      this.querySelector('cart-drawer-items')?.dispatchViewEvent();
    } catch (e) {
      // A throw before the setTimeout above would strand `opening` true and
      // leave the drawer permanently unopenable. Fail open, then re-throw so
      // the error still surfaces.
      this.opening = false;
      throw e;
    }
  }

  close() {
    this.classList.remove('active');
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden');
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  renderContents(parsedState) {
    this.querySelector('.drawer__inner').classList.contains('is-empty') &&
      this.querySelector('.drawer__inner').classList.remove('is-empty');
    this.productId = parsedState.id;

    /*
     * TECHNICAL DEBT — remove when morph.js lands.
     * renderContents() replaces .drawer__inner wholesale, destroying scroll
     * position and focus. morph.js patches in place and preserves both
     * natively; this save/restore is a stopgap, not a design.
     * See docs/superpowers/specs/2026-08-19-cart-drawer-design.md.
     */
    const scroller = this.querySelector('cart-drawer-items');
    const scrollTop = scroller ? scroller.scrollTop : 0;
    const focusedElement = document.activeElement;
    // Only treat focus as "ours to restore" when it was actually inside the
    // drawer to begin with (e.g. not the PDP's Add to cart button).
    const focusedInDrawer = focusedElement && this.contains(focusedElement);
    const activeId = focusedInDrawer ? focusedElement.id || null : null;
    const selectionStart = focusedInDrawer && typeof focusedElement.selectionStart === 'number'
      ? focusedElement.selectionStart
      : null;

    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);

      if (!sectionElement) return;
      sectionElement.innerHTML = this.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
    });

    const newScroller = this.querySelector('cart-drawer-items');
    if (newScroller) newScroller.scrollTop = scrollTop;
    // Gated on .active too, matching restoreScrollAndFocus()'s guard below
    // — never steal focus into a drawer that isn't open.
    if (activeId && this.classList.contains('active')) {
      // A removed line's id no longer exists post-refresh — getElementById
      // returns null, and this simply skips restoring focus.
      const restored = document.getElementById(activeId);
      if (restored) {
        restored.focus();
        // Dead in practice today: the drawer's quantity input is
        // type="number" (snippets/cart-drawer.liquid), and selectionStart
        // reads as null on that type rather than throwing, so this branch
        // never runs. Guarded correctly and left in case the input type
        // ever changes to text/tel.
        if (selectionStart !== null && restored.setSelectionRange) {
          restored.setSelectionRange(selectionStart, selectionStart);
        }
      }
    }

    // Also handled by the MutationObserver set up in the constructor,
    // which fires for this replacement too; called explicitly here as well
    // since it's cheap, idempotent, and keeps this method correct even if
    // the observer is ever removed.
    this.syncGiftCueToggle();

    setTimeout(() => {
      this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
      this.open();
    });
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
    ];
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);
